"use client";

import { useNoteLabel } from "@/hooks/use-notation";
import { useEffect, useState } from "react";
import { Check, ChevronRight, SkipForward, X } from "lucide-react";
import { Note } from "@/lib/music/note";
import type { PianoPressSequenceState } from "@/hooks/use-piano-press-sequence";
import type { PianoPressSetState } from "@/hooks/use-piano-press-set";
import type { LayoutCode } from "@/lib/virtuosa-layout/types";
import type { TutorialStep } from "./tutorial-data";

const ROOT_NAMES = [
  "Do",
  "Do♯",
  "Ré",
  "Ré♯",
  "Mi",
  "Fa",
  "Fa♯",
  "Sol",
  "Sol♯",
  "La",
  "La♯",
  "Si",
] as const;

interface TutorialPanelProps {
  step: TutorialStep;
  stepIndex: number;
  totalSteps: number;
  pressedCodes: ReadonlySet<LayoutCode>;
  // Owned by the parent so it can highlight only the next key of the sequence.
  sequence: PianoPressSequenceState;
  // Unordered progress, for chord steps.
  chordSet: PianoPressSetState;
  chordMidis: readonly number[];
  sweepRootSemi: number;
  onAdvance: () => void;
  onSkip: () => void;
}

export function TutorialPanel({
  step,
  stepIndex,
  totalSteps,
  pressedCodes,
  sequence,
  chordSet,
  chordMidis,
  sweepRootSemi,
  onAdvance,
  onSkip,
}: TutorialPanelProps) {
  return (
    <div className="rounded-xl border border-amber-300/30 bg-amber-300/[0.04] p-4">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-amber-200/70">
          Tutoriel · {stepIndex + 1} / {totalSteps}
        </span>

        <div className="flex items-center gap-3">
          {/* A per-step way out. Before this, the only escape from a step that
              would not complete was "Passer", which abandoned the whole
              tutorial and marked it done — so one stuck step cost the other
              eleven, permanently. */}
          {step.kind !== "info" ? (
            <button
              type="button"
              onClick={onAdvance}
              className="flex items-center gap-1 text-[11px] text-text-muted hover:text-text-secondary cursor-pointer"
            >
              <SkipForward size={11} />
              Étape suivante
            </button>
          ) : null}

          <button
            type="button"
            onClick={onSkip}
            className="flex items-center gap-1 text-[11px] text-text-muted hover:text-text-secondary cursor-pointer"
          >
            <X size={12} />
            Passer
          </button>
        </div>
      </div>

      <h3 className="mb-1 text-sm font-semibold text-text-primary">
        {step.title}
      </h3>
      <p className="mb-3 text-xs text-text-secondary leading-relaxed">
        {step.description}
      </p>

      <StepBody
        step={step}
        pressedCodes={pressedCodes}
        sequence={sequence}
        chordSet={chordSet}
        chordMidis={chordMidis}
        sweepRootSemi={sweepRootSemi}
        onAdvance={onAdvance}
      />
    </div>
  );
}

function StepBody({
  step,
  pressedCodes,
  sequence,
  chordSet,
  chordMidis,
  sweepRootSemi,
  onAdvance,
}: {
  step: TutorialStep;
  pressedCodes: ReadonlySet<LayoutCode>;
  sequence: PianoPressSequenceState;
  chordSet: PianoPressSetState;
  chordMidis: readonly number[];
  sweepRootSemi: number;
  onAdvance: () => void;
}) {
  if (step.kind === "info") {
    return <ContinueButton onAdvance={onAdvance} label="Continuer" />;
  }

  if (step.kind === "press-code") {
    return (
      <PressCodeBody
        key={step.id}
        target={step.target}
        pressedCodes={pressedCodes}
        onAdvance={onAdvance}
      />
    );
  }

  if (step.kind === "press-chord") {
    return (
      <PressChordBody
        key={step.id}
        chordSet={chordSet}
        chordMidis={chordMidis}
        onAdvance={onAdvance}
      />
    );
  }

  if (step.kind === "chord-sweep") {
    return (
      <div className="space-y-2">
        <div className="flex flex-wrap gap-1.5">
          {ROOT_NAMES.map((name, i) => (
            <span
              key={name}
              className={`rounded-md border px-2 py-1 font-mono text-[11px] transition-all duration-150 ${
                i === sweepRootSemi
                  ? "border-amber-300/70 bg-amber-300/15 text-amber-200"
                  : "border-border-subtle bg-bg-elevated/60 text-text-muted"
              }`}
            >
              {name}
            </span>
          ))}
        </div>
        <ContinueButton onAdvance={onAdvance} label="J'ai vu" />
      </div>
    );
  }

  return (
    <PlaySequenceBody
      key={step.id}
      step={step}
      sequence={sequence}
      onAdvance={onAdvance}
    />
  );
}

function ContinueButton({
  onAdvance,
  label,
}: {
  onAdvance: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onAdvance}
      className="flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-bright transition-colors cursor-pointer"
    >
      {label}
      <ChevronRight size={14} />
    </button>
  );
}

function PressCodeBody({
  target,
  pressedCodes,
  onAdvance,
}: {
  target: LayoutCode;
  pressedCodes: ReadonlySet<LayoutCode>;
  onAdvance: () => void;
}) {
  // Latch the hit: a key press is usually released well before the 350 ms
  // celebration delay, and reacting to the live `pressed` state would cancel
  // the pending advance on key-up.
  const [done, setDone] = useState(false);

  if (!done && pressedCodes.has(target)) {
    setDone(true);
  }

  useEffect(() => {
    if (!done) {
      return;
    }

    const id = window.setTimeout(onAdvance, 350);

    return () => window.clearTimeout(id);
  }, [done, onAdvance]);

  return (
    <div className="flex items-center gap-2 text-xs">
      <StatusDot done={done} />
      <span className={done ? "text-ok" : "text-text-muted"}>
        {done ? "Bien joué !" : "En attente de la touche surlignée…"}
      </span>
    </div>
  );
}

function PressChordBody({
  chordSet,
  chordMidis,
  onAdvance,
}: {
  chordSet: PianoPressSetState;
  chordMidis: readonly number[];
  onAdvance: () => void;
}) {
  const toNoteLabel = useNoteLabel();
  const { hit, complete } = chordSet;

  useEffect(() => {
    if (!complete) {
      return;
    }

    const id = window.setTimeout(onAdvance, 500);

    return () => window.clearTimeout(id);
  }, [complete, onAdvance]);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {chordMidis.map((midi) => {
          const reached = hit.has(midi);
          const name = Note.fromMidi(midi).toName({
            preferSharps: true,
            withOctave: true,
          });

          return (
            <span
              key={midi}
              className={`rounded-md border px-2 py-1 font-mono text-[11px] transition-all duration-150 ${
                reached
                  ? "border-ok bg-ok/15 text-ok"
                  : "border-amber-300/70 bg-amber-300/15 text-amber-200"
              }`}
            >
              {toNoteLabel(name)}
            </span>
          );
        })}
      </div>

      <div className="flex items-center gap-2 text-xs">
        <StatusDot done={complete} />
        <span className={complete ? "text-ok" : "text-text-muted"}>
          {complete
            ? "Voilà la forme."
            : `${hit.size} / ${chordMidis.length} — l'ordre n'a pas d'importance`}
        </span>
      </div>
    </div>
  );
}

function PlaySequenceBody({
  step,
  sequence,
  onAdvance,
}: {
  step: Extract<TutorialStep, { kind: "play-sequence" }>;
  sequence: PianoPressSequenceState;
  onAdvance: () => void;
}) {
  const toNoteLabel = useNoteLabel();
  const { progress, complete } = sequence;

  // A wrong note sends the cursor back to zero, silently. Counting those
  // returns is the only way to notice a learner is stuck, and after two the
  // step says so and offers the way out rather than looping forever.
  //
  // Compared during render rather than in an effect — the same pattern
  // usePianoPressSequence uses to reset on a new target — because an effect
  // here would count the stumble one render after the reset was already on
  // screen.
  const [stumbles, setStumbles] = useState(0);
  const [seenProgress, setSeenProgress] = useState(progress);

  if (seenProgress !== progress) {
    setSeenProgress(progress);

    if (seenProgress > 0 && progress === 0) {
      setStumbles((n) => n + 1);
    }
  }

  useEffect(() => {
    if (!complete) {
      return;
    }

    const id = window.setTimeout(onAdvance, 500);

    return () => window.clearTimeout(id);
  }, [complete, onAdvance]);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {step.noteNames.map((name, i) => {
          const reached = i < progress;
          const current = i === progress && !complete;

          return (
            <span
              key={`${name}-${i}`}
              className={`rounded-md border px-2 py-1 font-mono text-[11px] transition-all duration-150 ${
                reached
                  ? "border-ok bg-ok/15 text-ok"
                  : current
                    ? "border-amber-300/70 bg-amber-300/15 text-amber-200 motion-safe:animate-pulse"
                    : "border-border-subtle bg-bg-elevated/60 text-text-muted"
              }`}
            >
              {toNoteLabel(name)}
            </span>
          );
        })}
      </div>

      <div className="flex items-center gap-2 text-xs">
        <StatusDot done={complete} />
        <span className={complete ? "text-ok" : "text-text-muted"}>
          {complete
            ? "Parfait !"
            : `Note ${progress + 1} / ${step.noteNames.length}`}
        </span>
      </div>

      {stumbles >= 2 && !complete ? (
        <p className="text-[11px] text-text-muted italic">
          Une fausse note repart du début — c’est normal, la gamme zigzague.
          Tu peux passer à l’étape suivante et y revenir plus tard.
        </p>
      ) : null}
    </div>
  );
}

function StatusDot({ done }: { done: boolean }) {
  if (done) {
    return (
      <span className="flex h-4 w-4 items-center justify-center rounded-full bg-ok text-white">
        <Check size={10} />
      </span>
    );
  }

  return (
    <span className="h-2 w-2 rounded-full bg-amber-300 motion-safe:animate-pulse" />
  );
}
